import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Icon,
  Metric,
  Table,
  viLabel,
  type Column,
} from '@/components/primitives';
import { PageHeader } from '@/components/layout/PageHeader';
import { CalibrationBanner } from '@/components/common/CalibrationBanner';
import { useViewport } from '@/lib/viewport';
import { useScope } from '@/lib/scope';
import { useAuth } from '@/lib/auth';
import { useMockTable } from '@/hooks/useMockTable';
import { useForecastResults } from '@/hooks/useForecast';
import { forecastService } from '@/services/forecastService';
import { formatNumber, toISODate } from '@/lib/utils';
import type { ForecastResult } from '@/domain';

const HORIZON_DAYS = 14;

export function ForecastOverviewPage() {
  const { isMobile, vw } = useViewport();
  const { currentWarehouse } = useScope();
  const { user } = useAuth();
  const navigate = useNavigate();
  const calendar = useMockTable('day_calendar');
  const customers = useMockTable('customers');
  const allResults = useForecastResults({
    warehouseId: currentWarehouse?.id,
  });
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState<{ rows: number; at: string } | null>(null);

  const today = toISODate(new Date());
  const horizonEnd = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + HORIZON_DAYS - 1);
    return toISODate(d);
  }, [today]);

  const horizonResults = useMemo(
    () => allResults.filter((r) => r.date >= today && r.date <= horizonEnd),
    [allResults, today, horizonEnd],
  );

  const byDate = useMemo(() => {
    const m = new Map<string, ForecastResult[]>();
    for (const r of horizonResults) {
      const arr = m.get(r.date) ?? [];
      arr.push(r);
      m.set(r.date, arr);
    }
    return m;
  }, [horizonResults]);

  const dayRows = useMemo(() => {
    const out: {
      date: string;
      event_type: string;
      day_type: string;
      qty: number;
      workload_hours: number;
      avg_wlu: number;
      low_conf: number;
      total: number;
    }[] = [];
    for (let i = 0; i < HORIZON_DAYS; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() + i);
      const iso = toISODate(d);
      const rs = byDate.get(iso) ?? [];
      const cal = calendar.find((c) => c.date === iso);
      const qty = rs.reduce((s, r) => s + r.forecast_qty, 0);
      const hours = rs.reduce((s, r) => s + r.total_workload_hours, 0);
      const avg = rs.length > 0 ? rs.reduce((s, r) => s + r.wlu_per_order_minutes, 0) / rs.length : 0;
      const low = rs.filter((r) => r.confidence.is_low_confidence).length;
      out.push({
        date: iso,
        event_type: cal?.event_type ?? 'NORMAL',
        day_type: cal?.day_type ?? 'WEEKDAY',
        qty,
        workload_hours: hours,
        avg_wlu: avg,
        low_conf: low,
        total: rs.length,
      });
    }
    return out;
  }, [byDate, calendar, today]);

  const totalQty = dayRows.reduce((s, r) => s + r.qty, 0);
  const totalHours = dayRows.reduce((s, r) => s + r.workload_hours, 0);
  const lowConfCount = horizonResults.filter((r) => r.confidence.is_low_confidence).length;
  const maxQty = Math.max(1, ...dayRows.map((r) => r.qty));
  const peakDay = dayRows.length > 0
    ? dayRows.reduce((max, r) => (r.qty > max.qty ? r : max), dayRows[0]!)
    : null;

  async function runForecast() {
    if (!currentWarehouse || !user) return;
    setRunning(true);
    try {
      const r = await forecastService.run({
        warehouse_id: currentWarehouse.id,
        start_date: today,
        end_date: horizonEnd,
        actor: user.email,
      });
      setLastRun({ rows: r.rows.length, at: new Date().toISOString() });
    } finally {
      setRunning(false);
    }
  }

  const cols: Column<(typeof dayRows)[number]>[] = [
    {
      header: 'Ngày',
      mono: true,
      render: (r) => (
        <div>
          <div style={{ fontWeight: 600, color: 'var(--bx-text-dark)' }}>{r.date.slice(5)}</div>
          <div style={{ fontSize: 10.5, color: 'var(--bx-text-muted)' }}>{r.date.slice(0, 4)}</div>
        </div>
      ),
    },
    {
      header: 'Loại ngày',
      render: (r) => (
        <Badge kind={r.event_type === 'NORMAL' ? 'DRAFT' : r.event_type === 'MEGA_SALE' ? 'CRITICAL' : 'WARNING'}>
          {viLabel(r.event_type)}
        </Badge>
      ),
    },
    {
      header: 'Đơn dự báo',
      align: 'right',
      mono: true,
      render: (r) => (r.total === 0 ? <span style={{ color: 'var(--bx-text-muted)' }}>—</span> : formatNumber(r.qty)),
    },
    {
      header: 'Workload (giờ)',
      align: 'right',
      mono: true,
      priority: 2,
      render: (r) =>
        r.total === 0 ? '—' : r.workload_hours.toFixed(1),
    },
    {
      header: 'Phút/đơn TB',
      align: 'right',
      mono: true,
      priority: 2,
      render: (r) => (r.total === 0 ? '—' : r.avg_wlu.toFixed(2)),
    },
    {
      header: 'KH',
      align: 'right',
      mono: true,
      width: 60,
      priority: 3,
      render: (r) => r.total,
    },
    {
      header: 'Độ tin cậy',
      priority: 2,
      render: (r) =>
        r.total === 0 ? (
          <span style={{ fontSize: 11.5, color: 'var(--bx-text-muted)' }}>chưa chạy</span>
        ) : r.low_conf > 0 ? (
          <Badge kind="WARNING" size="sm">
            {r.low_conf} thấp
          </Badge>
        ) : (
          <Badge kind="APPROVED" size="sm">
            Tốt
          </Badge>
        ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader
        breadcrumb={['Lập kế hoạch', 'Dự báo']}
        title="Dự báo"
        subtitle={
          currentWarehouse
            ? `${currentWarehouse.code} · ${currentWarehouse.name} · ${HORIZON_DAYS} ngày tới`
            : 'Chọn kho ở thanh trên'
        }
        actions={
          <>
            <Button
              variant="outline"
              icon="users"
              onClick={() => navigate('/planner/forecast/customer')}
            >
              Khách hàng
            </Button>
            <Button
              variant="outline"
              icon="warehouse"
              onClick={() => navigate('/planner/forecast/warehouse')}
            >
              Báo cáo kho
            </Button>
            <Button
              variant="primary"
              icon="rocket"
              disabled={!currentWarehouse || running}
              onClick={runForecast}
            >
              {running
                ? 'Đang chạy…'
                : horizonResults.length === 0
                  ? 'Chạy dự báo'
                  : 'Chạy lại'}
            </Button>
          </>
        }
      />

      <div style={{ flex: 1, padding: isMobile ? 14 : 20 }}>
        <CalibrationBanner subtitle="History window 2025-05-22 → 2026-05-22 (1 năm). Forecast tự bật cờ ⚠️ low-confidence khi sample_size < 5 — kiểm tra cột 'Độ tin cậy' bên dưới." />
        {/* KPI strip */}
        <div
          style={{
            display: 'grid',
            gap: 12,
            marginBottom: 16,
            gridTemplateColumns: vw < 720 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
          }}
        >
          <Metric
            label={`Tổng đơn ${HORIZON_DAYS} ngày`}
            value={formatNumber(totalQty)}
            sublabel="đơn dự báo"
            hint={lastRun ? `Chạy lúc ${new Date(lastRun.at).toLocaleTimeString('vi-VN')}` : undefined}
          />
          <Metric
            label="Workload tổng"
            value={formatNumber(Math.round(totalHours))}
            sublabel="giờ pack"
            delta={`${(totalHours / 8).toFixed(0)} ca · 8h`}
            deltaTone="flat"
          />
          <Metric
            label="Ngày peak"
            value={peakDay ? formatNumber(peakDay.qty) : '—'}
            sublabel={peakDay ? peakDay.date.slice(5) + ' · ' + viLabel(peakDay.event_type) : '—'}
            deltaTone="up"
          />
          <Metric
            label="Low-confidence rows"
            value={lowConfCount}
            sublabel={`của ${horizonResults.length} rows`}
            deltaTone={lowConfCount > 0 ? 'down' : 'flat'}
            delta={
              horizonResults.length > 0
                ? `${Math.round((lowConfCount / horizonResults.length) * 100)}%`
                : '—'
            }
          />
        </div>

        {/* Run prompt if no forecast yet */}
        {horizonResults.length === 0 && (
          <Card>
            <div
              style={{
                display: 'flex',
                gap: 16,
                alignItems: 'center',
                padding: '12px 4px',
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  background: '#eef2ff',
                  color: 'var(--bx-primary)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Icon name="forecast" size={20} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--bx-text-heading)',
                    marginBottom: 4,
                  }}
                >
                  Chưa có dự báo cho 14 ngày tới
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--bx-text-muted)' }}>
                  Bấm "Chạy dự báo" để engine tính {customers.filter((c) => c.is_active).length} ×{' '}
                  {HORIZON_DAYS} = {customers.filter((c) => c.is_active).length * HORIZON_DAYS}{' '}
                  rows · dùng 90 ngày lịch sử + tham số mùa/sự kiện hiện hành.
                </div>
              </div>
              <Button
                variant="primary"
                icon="rocket"
                disabled={!currentWarehouse || running}
                onClick={runForecast}
              >
                {running ? 'Đang chạy…' : 'Chạy dự báo ngay'}
              </Button>
            </div>
          </Card>
        )}

        {/* Bar chart */}
        {horizonResults.length > 0 && (
          <Card
            title="Đơn dự báo theo ngày"
            subtitle={`${HORIZON_DAYS} ngày · từ ${today} đến ${horizonEnd}`}
            style={{ marginBottom: 16 }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 6,
                height: 200,
                padding: '12px 0 8px',
              }}
            >
              {dayRows.map((d) => {
                const h = d.qty > 0 ? (d.qty / maxQty) * 160 : 4;
                const isPeak = d.event_type === 'MEGA_SALE' || d.event_type === 'DOUBLE_DAY';
                const isLift = ['PAYDAY', 'MID_MONTH', 'MINI_SPIKE'].includes(d.event_type);
                const tone = isPeak ? '#b91c1c' : isLift ? '#d97706' : '#0b43ea';
                return (
                  <div
                    key={d.date}
                    style={{
                      flex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        fontFamily: 'var(--bx-font-mono)',
                        fontWeight: 700,
                        color: tone,
                      }}
                    >
                      {d.qty > 0 ? formatNumber(d.qty) : '—'}
                    </div>
                    <div
                      style={{
                        width: '100%',
                        maxWidth: 36,
                        height: h,
                        background:
                          d.qty > 0
                            ? isPeak
                              ? 'linear-gradient(180deg, #fecaca 0%, #fee2e2 100%)'
                              : isLift
                                ? 'linear-gradient(180deg, #fde68a 0%, #fefce8 100%)'
                                : 'linear-gradient(180deg, #c7d2fe 0%, #eef2ff 100%)'
                            : 'var(--bx-surface-2)',
                        border: `1.5px solid ${d.qty > 0 ? tone : 'var(--bx-border-muted)'}`,
                        borderRadius: 6,
                      }}
                    />
                    <div
                      style={{
                        fontSize: 10.5,
                        fontWeight: 500,
                        color: 'var(--bx-text-muted)',
                      }}
                    >
                      {d.date.slice(8)}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Day table */}
        <Card
          title="Chi tiết theo ngày"
          subtitle="Click một ngày để xem breakdown theo khách hàng (Phase M6)"
          padding={0}
        >
          <Table columns={cols} rows={dayRows} getRowKey={(r) => r.date} />
        </Card>
      </div>
    </div>
  );
}
